// Recuperacao de orcamentos perdidos (21/10/2026).
//
// Pagina a parte que alimenta a aba de orcamentos. Desenho aprovado pelo Leandro
// em 21/10/2026 (prototipo "Recuperacao de Orcamentos", versao 3):
// - fila de ligacoes por valor, do maior para o menor, 10 por dia;
// - painel com retornos atrasados, retornos de hoje, ligacoes feitas e recuperado;
// - "Registrar ligacao" grava uma interacao no orcamento do CRM (canal "Ligação"),
//   pela rota que ja existia, com resultado, com quem falou e data de retorno;
// - ficha de cada orcamento: resumo da negociacao (do orcamento) e contato,
//   telefone, e-mail, site e informacoes do cliente (valem para todos os
//   orcamentos do mesmo cliente). O lapis abre so a ficha.
// Decisoes dele: fila livre (quem ligar fica registrado), todos veem a fila
// inteira, "proximo retorno" = retorno da ULTIMA ligacao, "Recuperado" so registra.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const RESULTADOS = {
  nao_atendeu: { rotulo: 'Não atendeu', classe: 'bg-surface-mid border-line text-ink-soft' },
  falou: { rotulo: 'Falou', classe: 'bg-info border-info-line text-info-ink' },
  pedir_retorno: { rotulo: 'Pedir retorno', classe: 'bg-warn border-warn-line text-warn-ink' },
  recuperado: { rotulo: 'Recuperado', classe: 'bg-ok border-ok-line text-ok-ink' },
  sem_interesse: { rotulo: 'Sem interesse', classe: 'bg-risk border-risk-line text-risk-ink' },
};
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const brl0 = (v) => `R$ ${Math.round(Number(v || 0)).toLocaleString('pt-BR')}`;
const diaMes = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
const dataHora = (iso) => {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const hojeLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const rotuloDia = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : `${DIAS_SEMANA[d.getDay()]} ${diaMes(iso)}`;
};
const separar = (txt, sep) => String(txt || '').split(sep).map((x) => x.trim()).filter(Boolean);
// Ultima ligacao = a interacao mais recente COM resultado (a lista ja vem da mais
// nova para a mais antiga). Interacao sem resultado (WhatsApp etc.) nao conta.
const ultimaLigacao = (item) => (item.interacoes || []).find((i) => i.resultado) || null;
const ligacoes = (item) => (item.interacoes || []).filter((i) => i.resultado);
const linkDoSite = (site) => (/^https?:\/\//i.test(site) ? site : `https://${site}`);
const dataBR = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
const diaIso = (v) => {
  const s = String(v || '');
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
// Situacao de compra do cliente em relacao a ESTE orcamento (pedido do Leandro
// em 21/10: nao tratar igual quem perdeu um orcamento mas continua comprando).
// Compara a data de saida da ultima compra concluida com a data do orcamento.
const situacaoCompra = (item) => {
  const c = item.ficha?.compras;
  if (!c) return null;
  const ult = c.ultima;
  if (!ult || !ult.data_saida) return { tipo: 'nunca', rotulo: `Nenhuma compra concluída desde ${dataBR(c.janela?.inicio)}`, classe: 'bg-surface-mid border-line text-ink-soft' };
  const dOrc = diaIso(item.orcamento?.requested_at);
  if (dOrc && ult.data_saida.slice(0, 10) > dOrc) return { tipo: 'depois', rotulo: `Comprou depois deste orçamento · última ${dataBR(ult.data_saida)}`, classe: RESULTADOS.recuperado.classe };
  return { tipo: 'antes', rotulo: `Sem compra desde o orçamento · última ${dataBR(ult.data_saida)}`, classe: RESULTADOS.pedir_retorno.classe };
};

const Chip = ({ classe, children, tracejado }) => (
  <span className={`text-[11.5px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${tracejado ? 'border-dashed' : ''} ${classe}`}>{children}</span>
);

const IconeLapis = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.21a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
);

function BotaoCopiar({ valor, aviso }) {
  return (
    <button
      type="button"
      className="shrink-0 px-1.5 py-0.5 rounded-md border border-line bg-surface-card text-brand-ink text-[11px] font-semibold"
      onClick={() => {
        const ok = () => aviso(`Copiado: ${valor}`);
        try { navigator.clipboard.writeText(valor).then(ok, () => aviso(valor)); } catch (e) { aviso(valor); }
      }}
    >Copiar</button>
  );
}

// Configurar a IA do Copiloto (21/10/2026, 5o ciclo): so admin. A chave vai
// para um arquivo protegido no servidor; a tela nunca recebe a chave de volta,
// so os 4 ultimos caracteres. Antes de gravar, o servidor testa no Groq.
function ConfigurarIA({ chamar, avisar }) {
  const [aberto, setAberto] = useState(false);
  const [info, setInfo] = useState(null);
  const [chave, setChave] = useState('');
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const ler = useCallback(async () => {
    try { setInfo(await chamar('GET', '/config/ia')); setErro(''); } catch (e) { setErro(e.message); }
  }, [chamar]);
  useEffect(() => { if (aberto && !info) ler(); }, [aberto, info, ler]);
  const acao = async (fn, msg) => {
    setOcupado(true); setErro('');
    try { const d = await fn(); if (d && d.configurada !== undefined) setInfo(d); if (msg) avisar(msg); } catch (e) { setErro(e.message); }
    setOcupado(false);
  };
  return (
    <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4">
      <button type="button" aria-expanded={aberto} onClick={() => setAberto(!aberto)} className="text-sm font-bold text-ink">{aberto ? '▾' : '▸'} Configurar IA do Copiloto <span className="font-normal text-ink-faint">· só administrador</span></button>
      {aberto ? (
        <div className="mt-3 flex flex-col gap-2 text-sm">
          {info ? (
            <p className="text-ink-soft">
              {info.configurada
                ? <>Chave cadastrada, terminando em <b className="tabular-nums">…{info.final}</b> ({info.origem === 'tela' ? 'cadastrada por esta tela' : 'do arquivo .env do servidor'}). Modelo: {info.modelo}.</>
                : 'Nenhuma chave cadastrada: o botão "Preparar ligação" mostra "IA não configurada".'}
              {info.alterado_por ? ` Última alteração: ${info.acao === 'removeu' ? 'removida' : 'gravada'} por ${info.alterado_por} em ${dataHora(info.alterado_em)}.` : ''}
            </p>
          ) : <p className="text-ink-soft">Lendo...</p>}
          <label className="flex flex-col gap-1"><span className="text-xs font-bold text-ink-soft">Nova chave do Groq <span className="font-normal text-ink-faint">· console.groq.com → API Keys</span></span>
            <input type="password" autoComplete="off" className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-card text-ink" value={chave} onChange={(e) => setChave(e.target.value)} placeholder="gsk_..." />
          </label>
          <p className="text-[11.5px] text-ink-faint">A chave é testada no Groq antes de gravar e fica num arquivo protegido no servidor, fora da planilha e dos backups. Ela não aparece de novo na tela.</p>
          {erro ? <p className="font-semibold text-risk-ink">{erro}</p> : null}
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={ocupado || !chave.trim()} onClick={() => acao(async () => { const d = await chamar('PUT', '/config/ia', { chave }); setChave(''); return d; }, 'Chave testada e gravada')} className="px-4 py-2 rounded-full bg-brand-600 text-white text-sm font-bold disabled:opacity-60">{ocupado ? 'Aguarde...' : 'Testar e gravar'}</button>
            {info && info.configurada ? <button type="button" disabled={ocupado} onClick={() => acao(() => chamar('POST', '/config/ia/testar', {}), 'A chave atual funciona')} className="px-4 py-2 rounded-full border border-line bg-surface-card text-brand-ink text-sm font-bold">Testar a atual</button> : null}
            {info && info.origem === 'tela' ? <button type="button" disabled={ocupado} onClick={() => acao(() => chamar('DELETE', '/config/ia'), 'Chave removida')} className="px-4 py-2 rounded-full border border-risk-line bg-risk text-risk-ink text-sm font-bold">Remover chave</button> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Recuperacao({ apiUrl, token, user, users = [], isAdmin = false, onVoltar }) {
  const [itens, setItens] = useState([]);
  const [tirados, setTirados] = useState([]);
  const [verTirados, setVerTirados] = useState(false);
  const [tirando, setTirando] = useState(null); // item que esta sendo tirado da fila
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [dia, setDia] = useState('');
  const [filtroVendedor, setFiltroVendedor] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');
  const [aberto, setAberto] = useState(null); // { item, soFicha }
  const [aviso, setAviso] = useState('');
  const hoje = hojeLocal();

  const relogioAviso = useRef(null);
  const avisar = useCallback((txt) => {
    setAviso(txt);
    window.clearTimeout(relogioAviso.current);
    relogioAviso.current = window.setTimeout(() => setAviso(''), 2800);
  }, []);

  const chamar = useCallback(async (metodo, rota, corpo) => {
    const res = await fetch(`${apiUrl}${rota}`, {
      method: metodo,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    });
    const dados = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = res.status === 401 ? 'Sua sessão expirou. Entre de novo no CRM.' : (dados.error || `Erro ${res.status}`);
      throw new Error(msg);
    }
    return dados;
  }, [apiUrl, token]);

  const carregar = useCallback(async () => {
    try {
      const dados = await chamar('GET', '/recuperacao');
      setItens(Array.isArray(dados.itens) ? dados.itens : []);
      setTirados(Array.isArray(dados.tirados) ? dados.tirados : []);
      setErro('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [chamar]);

  useEffect(() => { carregar(); }, [carregar]);

  const dias = useMemo(() => [...new Set(itens.map((i) => i.dia_previsto).filter(Boolean))].sort(), [itens]);
  useEffect(() => {
    if (dia || !dias.length) return;
    setDia(dias.includes(hoje) ? hoje : (dias.find((d) => d >= hoje) || ''));
  }, [dias, dia, hoje]);

  const painel = useMemo(() => {
    let feitas = 0; let recuperado = 0; let recuperadoN = 0;
    const retornos = [];
    itens.forEach((item) => {
      const ult = ultimaLigacao(item);
      if (!ult) return;
      feitas += 1;
      if (ult.resultado === 'recuperado') { recuperado += Number(item.orcamento?.budget_value || 0); recuperadoN += 1; }
      if (ult.retorno_em) retornos.push({ item, ult });
    });
    retornos.sort((a, b) => a.ult.retorno_em.localeCompare(b.ult.retorno_em));
    return {
      feitas, recuperado, recuperadoN, retornos,
      atrasados: retornos.filter((r) => r.ult.retorno_em < hoje).length,
      deHoje: retornos.filter((r) => r.ult.retorno_em === hoje).length,
    };
  }, [itens, hoje]);

  const nomesVendedores = useMemo(() => {
    const doCadastro = users.map((u) => String(u.name || '').trim()).filter(Boolean);
    const daFila = itens.map((i) => i.vendedor).filter(Boolean);
    const meu = String(user?.name || '').trim();
    return [...new Set([...doCadastro, ...daFila, meu].filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [users, itens, user]);

  const lista = useMemo(() => itens.filter((i) => {
    if (dia && i.dia_previsto !== dia) return false;
    if (filtroVendedor === '-' && i.vendedor) return false;
    if (filtroVendedor && filtroVendedor !== '-' && i.vendedor !== filtroVendedor) return false;
    const ult = ultimaLigacao(i);
    if (filtroSituacao === 'pend' && ult) return false;
    if (filtroSituacao === 'feito' && !ult) return false;
    if (filtroSituacao === 'comprou' && situacaoCompra(i)?.tipo !== 'depois') return false;
    return true;
  }), [itens, dia, filtroVendedor, filtroSituacao]);

  const totalLista = lista.reduce((s, i) => s + Number(i.orcamento?.budget_value || 0), 0);

  if (carregando) return <section className="p-6 text-ink-soft">Carregando a fila de recuperação...</section>;

  return (
    <section className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={onVoltar} className="text-sm font-semibold text-brand-ink">← Voltar para Orçamentos</button>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-ink-faint font-bold">Alimenta a aba Orçamentos</p>
        <h2 className="mt-1 text-2xl font-black text-ink">Recuperação de orçamentos perdidos</h2>
        <p className="mt-1 text-sm text-ink-soft max-w-3xl">
          Fila de ligações por valor, do maior para o menor. Cada ligação registrada vira uma interação no orçamento do CRM, com resultado, com quem falou e data de retorno.
        </p>
      </div>

      {isAdmin ? <ConfigurarIA chamar={chamar} avisar={avisar} /> : null}

      {erro && (
        <div className="rounded-xl border border-risk-line bg-risk text-risk-ink px-4 py-3 text-sm font-semibold flex items-center gap-3 flex-wrap">
          <span>{erro}</span>
          <button type="button" className="underline" onClick={() => { setCarregando(true); carregar(); }}>Tentar de novo</button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4"><p className="text-[11.5px] font-bold uppercase tracking-wide text-risk-ink">Retornos atrasados</p><p className="mt-1 text-2xl font-black text-ink tabular-nums">{painel.atrasados}</p><p className="text-xs text-ink-soft">retorno marcado antes de hoje</p></div>
        <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4"><p className="text-[11.5px] font-bold uppercase tracking-wide text-warn-ink">Retornos de hoje</p><p className="mt-1 text-2xl font-black text-ink tabular-nums">{painel.deHoje}</p><p className="text-xs text-ink-soft">combinados para {diaMes(hoje)}</p></div>
        <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4"><p className="text-[11.5px] font-bold uppercase tracking-wide text-info-ink">Ligações feitas</p><p className="mt-1 text-2xl font-black text-ink tabular-nums">{painel.feitas}</p><p className="text-xs text-ink-soft">de {itens.length} orçamentos na fila</p></div>
        <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4"><p className="text-[11.5px] font-bold uppercase tracking-wide text-ok-ink">Recuperado</p><p className="mt-1 text-2xl font-black text-ink tabular-nums">{brl0(painel.recuperado)}</p><p className="text-xs text-ink-soft">{painel.recuperadoN} orçamento(s) marcado(s) como recuperado</p></div>
      </div>

      <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4">
        <div className="flex items-baseline gap-3 flex-wrap mb-3">
          <h3 className="text-base font-bold text-ink">Precisa de retorno</h3>
          <span className="text-xs text-ink-soft">Próximo retorno = a data marcada na última ligação de cada orçamento. Atrasados primeiro.</span>
        </div>
        {painel.retornos.length ? (
          <div className="flex flex-col gap-2">
            {painel.retornos.map(({ item, ult }) => {
              const atrasado = ult.retorno_em < hoje; const deHoje = ult.retorno_em === hoje;
              return (
                <div key={item.budget_id} className="flex items-center gap-3 flex-wrap px-3 py-2 rounded-xl border border-line bg-surface-low">
                  <span className="text-xs font-bold tabular-nums w-12">{diaMes(ult.retorno_em)}</span>
                  <span className="font-semibold text-ink min-w-0 flex-1 truncate">
                    {item.orcamento?.client_name || item.ficha?.nome_cliente || '—'}
                    <span className="font-normal text-ink-soft text-xs"> · orç. {item.orcamento_erp} · {brl(item.orcamento?.budget_value)}{ult.falou_com ? ` · ${ult.falou_com}` : ''}</span>
                  </span>
                  <Chip classe={atrasado ? RESULTADOS.sem_interesse.classe : deHoje ? RESULTADOS.pedir_retorno.classe : RESULTADOS.falou.classe}>{atrasado ? 'Atrasado' : deHoje ? 'Hoje' : 'Agendado'}</Chip>
                  <button type="button" onClick={() => setAberto({ item, soFicha: false })} className="px-3 py-1.5 rounded-full border border-line bg-surface-card text-brand-ink text-sm font-bold">Registrar ligação</button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-ink-soft">Nenhum retorno marcado. Quando uma ligação terminar em "Pedir retorno", ela aparece aqui na data combinada.</p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-base font-bold text-ink">Fila de ligações</h3>
          <span className="text-xs text-ink-soft tabular-nums">{lista.length} orçamento(s) · {brl(totalLista)} em jogo</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {dias.map((d) => {
            const doDia = itens.filter((i) => i.dia_previsto === d);
            const feitos = doDia.filter((i) => ultimaLigacao(i)).length;
            return (
              <button key={d} type="button" aria-pressed={dia === d} onClick={() => setDia(d)}
                className={`px-3 py-1.5 rounded-full border text-sm font-semibold ${dia === d ? 'bg-brand-600 border-brand-600 text-white' : 'bg-surface-card border-line text-ink'}`}>
                {rotuloDia(d)} <span className={dia === d ? 'text-white/80' : 'text-ink-faint'}>{feitos}/{doDia.length}</span>
              </button>
            );
          })}
          <button type="button" aria-pressed={dia === ''} onClick={() => setDia('')}
            className={`px-3 py-1.5 rounded-full border text-sm font-semibold ${dia === '' ? 'bg-brand-600 border-brand-600 text-white' : 'bg-surface-card border-line text-ink'}`}>
            Todos <span className={dia === '' ? 'text-white/80' : 'text-ink-faint'}>{painel.feitas}/{itens.length}</span>
          </button>
          <span className="flex-1" />
          <label className="text-xs text-ink-soft" htmlFor="rec-vend">Vendedor</label>
          <select id="rec-vend" value={filtroVendedor} onChange={(e) => setFiltroVendedor(e.target.value)} className="px-3 py-2 border border-line rounded-xl text-sm bg-surface-card">
            <option value="">Todos</option>
            {nomesVendedores.map((n) => <option key={n} value={n}>{n}</option>)}
            <option value="-">Sem vendedor</option>
          </select>
          <label className="text-xs text-ink-soft" htmlFor="rec-sit">Situação</label>
          <select id="rec-sit" value={filtroSituacao} onChange={(e) => setFiltroSituacao(e.target.value)} className="px-3 py-2 border border-line rounded-xl text-sm bg-surface-card">
            <option value="">Todas</option>
            <option value="pend">Ainda não ligou</option>
            <option value="feito">Já ligou</option>
            <option value="comprou">Comprou depois deste orçamento</option>
          </select>
        </div>

        {!itens.length && !erro && (
          <div className="bg-surface-card border border-line rounded-2xl p-5 text-sm text-ink-soft">A fila está vazia. A carga dos orçamentos é feita pelo administrador.</div>
        )}

        {lista.map((item) => (
          <CardOrcamento key={item.budget_id} item={item} itens={itens} ult={ultimaLigacao(item)} nLig={ligacoes(item).length}
            aoLigar={() => setAberto({ item, soFicha: false })} aoEditar={() => setAberto({ item, soFicha: true })}
            aoCopiloto={() => setAberto({ item, soFicha: false, copiloto: true })} aoTirar={() => setTirando(item)} aviso={avisar} />
        ))}
        {itens.length > 0 && !lista.length && (
          <div className="bg-surface-card border border-line rounded-2xl p-5 text-sm text-ink-soft">Nenhum orçamento com esses filtros.</div>
        )}
      </div>

      {tirados.length ? (
        <div className="bg-surface-card border border-line rounded-2xl shadow-card p-4">
          <button type="button" aria-expanded={verTirados} onClick={() => setVerTirados(!verTirados)} className="text-base font-bold text-ink">
            {verTirados ? '▾' : '▸'} Tirados da fila ({tirados.length})
          </button>
          {verTirados ? (
            <div className="mt-3 flex flex-col gap-2">
              {tirados.map((t) => (
                <div key={t.budget_id} className="flex items-center gap-3 flex-wrap px-3 py-2 rounded-xl border border-line bg-surface-low text-sm">
                  <span className="min-w-0 flex-1 break-words">
                    <b>{t.cliente || '—'}</b> <span className="text-ink-soft text-xs">· orç. {t.orcamento_erp} · {brl(t.valor)}</span>
                    <span className="block text-xs text-ink-soft">Motivo: {t.removido_motivo || '—'} · tirado por {t.removido_por || '—'} em {dataHora(t.removido_em)}</span>
                  </span>
                  <button type="button" onClick={async () => {
                    try { await chamar('POST', `/recuperacao/devolver/${encodeURIComponent(t.budget_id)}`, {}); avisar(`Orçamento ${t.orcamento_erp} voltou para a fila`); await carregar(); } catch (e) { avisar(e.message); }
                  }} className="px-3 py-1.5 rounded-full border border-line bg-surface-card text-brand-ink text-sm font-bold">Voltar para a fila</button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {tirando && (
        <TirarDaFila item={tirando} chamar={chamar} aoFechar={() => setTirando(null)}
          aoTirar={async (msg) => { setTirando(null); avisar(msg); await carregar(); }} />
      )}

      {aberto && (
        <Gaveta
          item={aberto.item} soFicha={aberto.soFicha} copiloto={!!aberto.copiloto} itens={itens} hoje={hoje} user={user} nomesVendedores={nomesVendedores}
          chamar={chamar}
          aoFechar={() => setAberto(null)}
          aoSalvar={async (msg) => { setAberto(null); avisar(msg); await carregar(); }}
        />
      )}

      {aviso && (
        <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-5 z-50 bg-ink text-surface-card px-4 py-2 rounded-full text-sm font-semibold max-w-[calc(100%-32px)]">{aviso}</div>
      )}
    </section>
  );
}

// Tirar da fila (21/10/2026, 4o ciclo): todos os usuarios, motivo obrigatorio.
// Nao apaga nada: a linha fica guardada e pode voltar pela lista "Tirados da fila".
const MOTIVOS_TIRAR = ['Cliente já compra assiduamente', 'Recuperado / fechou', 'Não era orçamento', 'Não vale continuar'];
function TirarDaFila({ item, chamar, aoFechar, aoTirar }) {
  const [motivo, setMotivo] = useState('');
  const [outro, setOutro] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const final = motivo === 'outro' ? outro.trim() : motivo;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => !salvando && aoFechar()} />
      <div role="dialog" aria-modal="true" aria-label="Tirar da fila" className="relative w-full max-w-[420px] bg-surface-card border border-line rounded-2xl p-5 flex flex-col gap-3">
        <p className="font-bold text-ink">Tirar da fila: orçamento {item.orcamento_erp}</p>
        <p className="text-xs text-ink-soft">{item.orcamento?.client_name || item.ficha?.nome_cliente} · {brl(item.orcamento?.budget_value)}. Não apaga nada: as ligações e o orçamento continuam no CRM, e dá para voltar pela lista "Tirados da fila".</p>
        <div role="radiogroup" aria-label="Motivo" className="flex flex-col gap-1.5">
          {[...MOTIVOS_TIRAR, 'outro'].map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <input type="radio" name="motivo-tirar" checked={motivo === m} onChange={() => setMotivo(m)} />
              {m === 'outro' ? 'Outro' : m}
            </label>
          ))}
          {motivo === 'outro' ? <input className="w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-card text-ink" maxLength={200} value={outro} onChange={(e) => setOutro(e.target.value)} placeholder="Escreva o motivo" /> : null}
        </div>
        {erro ? <p className="text-sm font-semibold text-risk-ink">{erro}</p> : null}
        <div className="flex justify-end gap-2">
          <button type="button" disabled={salvando} onClick={aoFechar} className="px-4 py-2 rounded-full border border-line bg-surface-card text-brand-ink text-sm font-bold">Cancelar</button>
          <button type="button" disabled={salvando} onClick={async () => {
            if (!final) { setErro('Escolha o motivo.'); return; }
            setSalvando(true); setErro('');
            try { await chamar('POST', `/recuperacao/remover/${encodeURIComponent(item.budget_id)}`, { motivo: final }); await aoTirar(`Orçamento ${item.orcamento_erp} tirado da fila`); } catch (e) { setErro(e.message); setSalvando(false); }
          }} className="px-4 py-2 rounded-full border border-risk-line bg-risk text-risk-ink text-sm font-bold disabled:opacity-60">{salvando ? 'Tirando...' : 'Tirar da fila'}</button>
        </div>
      </div>
    </div>
  );
}

function CardOrcamento({ item, itens, ult, nLig, aoLigar, aoEditar, aoCopiloto, aoTirar, aviso }) {
  const o = item.orcamento || {};
  const f = item.ficha || {};
  const telsErp = separar(f.telefones_erp, ' / ');
  const emailsErp = separar(f.emails_erp, ' ; ');
  const anterior = (f.contatos_anteriores || [])[(f.contatos_anteriores || []).length - 1];
  const outros = itens.filter((x) => x.cod_cliente && x.cod_cliente === item.cod_cliente && x.budget_id !== item.budget_id)
    .map((x) => `${x.orcamento_erp} (#${x.ordem})`);
  const feito = ult && !['pedir_retorno', 'nao_atendeu'].includes(ult.resultado);
  const compra = situacaoCompra(item);
  const itensOrc = item.itens_orcamento || [];
  return (
    <article className={`bg-surface-card border border-line rounded-2xl shadow-card p-4 grid gap-4 grid-cols-[36px_minmax(0,1fr)] lg:grid-cols-[40px_minmax(0,1fr)_240px_170px] ${feito ? 'opacity-75' : ''}`}>
      <div className="text-xs font-extrabold text-ink-faint pt-1 tabular-nums">#{item.ordem}</div>
      <div className="min-w-0 flex flex-col gap-1.5">
        <div className="font-bold text-ink break-words">{o.client_name || f.nome_cliente || '—'}</div>
        <div className="text-xs text-ink-soft tabular-nums">Orçamento <b>{item.orcamento_erp}</b>{o.requested_at ? ` de ${new Date(o.requested_at).toLocaleDateString('pt-BR')}` : ''}{f.cidade ? ` · ${f.cidade}` : ''}</div>
        <div className="text-lg font-extrabold text-ink tabular-nums">{Number(o.budget_value || 0) ? brl(o.budget_value) : <span className="text-ink-soft text-sm">R$ 0,00 no orçamento</span>}</div>
        <div className="flex gap-1.5 flex-wrap items-center">
          {item.motivo_perda ? <Chip classe="bg-surface-mid border-line text-ink-soft">Perda: {item.motivo_perda}</Chip> : null}
          {ult ? <Chip classe={RESULTADOS[ult.resultado]?.classe}>{RESULTADOS[ult.resultado]?.rotulo}{ult.retorno_em ? ` · ${diaMes(ult.retorno_em)}` : ''}</Chip>
            : <Chip classe="bg-surface-mid border-line text-ink-soft">Ainda não ligou</Chip>}
          {compra ? <Chip classe={compra.classe}>{compra.rotulo}</Chip> : null}
        </div>
        {item.obs_perda ? <div className="text-xs text-ink-soft border-l-2 border-line pl-2 break-words">Vendedor no AM Gestor: “{item.obs_perda}”</div> : null}
        <div className="rounded-xl border border-line bg-surface-low px-3 py-2 text-xs flex flex-col gap-1">
          <div className="flex gap-2 min-w-0"><span className="w-28 shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Resumo</span><span className={`min-w-0 break-words ${item.resumo ? 'text-ink' : 'text-ink-faint'}`}>{item.resumo || 'Sem resumo ainda'}</span></div>
          {itensOrc.length ? (
            <div className="flex gap-2 min-w-0"><span className="w-28 shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Itens cotados</span>
              <span className="min-w-0 break-words text-ink">{itensOrc.slice(0, 3).map((x) => `${x.qtd ?? ''}× ${x.codigo || x.descricao || '—'}`).join(' · ')}{itensOrc.length > 3 ? ` · +${itensOrc.length - 3}` : ''}</span></div>
          ) : null}
          {item.ficha?.compras ? (
            <div className="flex gap-2 min-w-0"><span className="w-28 shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Compras 12 meses</span>
              <span className="min-w-0 break-words text-ink tabular-nums">{item.ficha.compras.m12?.pedidos ? `${item.ficha.compras.m12.pedidos} pedido(s) · ${brl(item.ficha.compras.m12.valor)}` : 'nenhuma'}</span></div>
          ) : null}
          <div className="flex gap-2 min-w-0"><span className="w-28 shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Site</span>{f.site ? <a className="min-w-0 break-all font-semibold text-brand-ink" href={linkDoSite(f.site)} target="_blank" rel="noopener noreferrer">{f.site}</a> : <span className="text-ink-faint">Não informado</span>}</div>
          <div className="flex gap-2 min-w-0"><span className="w-28 shrink-0 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Info. do cliente</span><span className={`min-w-0 break-words ${f.info ? 'text-ink' : 'text-ink-faint'}`}>{f.info || 'Nada anotado'}</span></div>
        </div>
        {outros.length ? <div className="text-xs text-info-ink">Mesmo cliente em outro orçamento da fila: {outros.join(', ')}</div> : null}
      </div>
      <div className="col-start-2 lg:col-start-auto min-w-0 flex flex-col gap-1 text-[12.5px]">
        <div className="rounded-xl border border-info-line bg-brand-50 px-3 py-2 mb-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Falar com</span>
            <button type="button" onClick={aoEditar} aria-label="Editar ficha do cliente" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-info-line bg-surface-card text-brand-ink text-[11px] font-bold"><IconeLapis /> Editar</button>
          </div>
          {f.contato ? <div className="text-base font-extrabold text-ink break-words">{f.contato}</div> : <div className="text-sm font-semibold text-warn-ink">Contato não cadastrado</div>}
          {anterior ? <div className="text-[11.5px] text-ink-soft">Antes: {anterior.nome || '—'} · trocado por {anterior.trocado_por} em {dataHora(anterior.trocado_em)}</div>
            : (f.contato && f.contato_origem === 'AM Gestor' ? <div className="text-[11.5px] text-ink-soft">do cadastro do AM Gestor</div> : null)}
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Telefone</span>
        {f.telefone ? <div className="flex items-center gap-1.5 min-w-0"><span className="tabular-nums break-all">{f.telefone}</span><span className="text-[10px] font-extrabold uppercase px-1 rounded border bg-ok border-ok-line text-ok-ink">ficha</span><BotaoCopiar valor={f.telefone} aviso={aviso} /></div> : null}
        {telsErp.map((t) => <div key={t} className="flex items-center gap-1.5 min-w-0"><span className="tabular-nums break-all">{t}</span><BotaoCopiar valor={t} aviso={aviso} /></div>)}
        {!f.telefone && !telsErp.length ? <span className="text-risk-ink">Sem telefone no cadastro</span> : null}
        <span className="mt-1 text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">E-mail</span>
        {f.email ? <div className="flex items-center gap-1.5 min-w-0"><span className="break-all">{f.email}</span><span className="text-[10px] font-extrabold uppercase px-1 rounded border bg-ok border-ok-line text-ok-ink">ficha</span></div> : null}
        {emailsErp.slice(0, 2).map((e) => <div key={e} className="break-all">{e}</div>)}
        {emailsErp.length > 2 ? <div className="text-ink-soft">+{emailsErp.length - 2} e-mail(s)</div> : null}
        {!f.email && !emailsErp.length ? <span className="text-ink-soft">Sem e-mail</span> : null}
      </div>
      <div className="col-start-2 lg:col-start-auto flex flex-col gap-2">
        <button type="button" onClick={aoLigar} className="px-4 py-2 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold">Registrar ligação</button>
        <button type="button" onClick={aoCopiloto} className="px-4 py-2 rounded-full border border-info-line bg-brand-50 text-brand-ink text-sm font-bold">{item.roteiro_ia ? 'Ver roteiro' : 'Preparar ligação'}</button>
        <div className="text-[11.5px] text-ink-soft">{ult ? `${nLig} ligaç${nLig === 1 ? 'ão' : 'ões'} · última ${dataHora(ult.interaction_at)} por ${ult.operator}` : 'Nenhuma ligação ainda'}</div>
        <div className="text-[11.5px] text-ink-soft">Vendedor: <b>{item.vendedor || '—'}</b></div>
        <button type="button" onClick={aoTirar} className="self-start text-[12px] font-semibold text-risk-ink underline">Tirar da fila</button>
      </div>
    </article>
  );
}

// Itens cotados no orcamento perdido e compras do cliente, copiados do AM Gestor
// (so leitura). A data da medicao aparece sempre: nao se atualiza sozinho.
function Produtos({ item }) {
  const itensOrc = item.itens_orcamento || [];
  const c = item.ficha?.compras;
  const compra = situacaoCompra(item);
  if (!itensOrc.length && !c) return null;
  const titulo = 'text-[11px] uppercase tracking-[0.14em] text-ink-faint font-extrabold';
  return (
    <div className="flex flex-col gap-3 pb-3 border-b border-line">
      {itensOrc.length ? (
        <div className="flex flex-col gap-1">
          <p className={titulo}>Itens cotados neste orçamento</p>
          <div className="rounded-lg border border-line overflow-hidden text-xs">
            {itensOrc.map((x, i) => (
              <div key={i} className={`grid grid-cols-[44px_minmax(0,1fr)_88px] gap-2 px-2 py-1.5 ${i % 2 ? 'bg-surface-low' : ''}`}>
                <span className="tabular-nums text-ink-soft">{x.qtd ?? ''}×</span>
                <span className="min-w-0 break-words"><b>{x.codigo || ''}</b>{x.descricao ? ` ${x.descricao}` : ''}</span>
                <span className="tabular-nums text-right">{x.valor_total != null ? brl(x.valor_total) : ''}</span>
              </div>
            ))}
          </div>
          <span className="text-[11px] text-ink-faint">Do orçamento no AM Gestor, copiado em {dataHora(item.itens_medido_em)}.</span>
        </div>
      ) : null}
      {c ? (
        <div className="flex flex-col gap-1">
          <p className={titulo}>Compras do cliente</p>
          {compra ? <div><Chip classe={compra.classe}>{compra.rotulo}</Chip></div> : null}
          <div className="text-xs text-ink-soft tabular-nums">
            {c.ultima ? <>Última compra: pedido {c.ultima.pedido} · saída {dataBR(c.ultima.data_saida)} · {brl(c.ultima.valor)}<br /></> : null}
            Últimos 12 meses: {c.m12?.pedidos || 0} pedido(s) · {brl(c.m12?.valor)} · 24 meses: {c.m24?.pedidos || 0} pedido(s) · {brl(c.m24?.valor)}
          </div>
          {(c.m12?.produtos || []).length ? (
            <div className="rounded-lg border border-line overflow-hidden text-xs">
              {c.m12.produtos.map((x, i) => (
                <div key={i} className={`grid grid-cols-[44px_minmax(0,1fr)_88px] gap-2 px-2 py-1.5 ${i % 2 ? 'bg-surface-low' : ''}`}>
                  <span className="tabular-nums text-ink-soft">{x.qtd}×</span>
                  <span className="min-w-0 break-words"><b>{x.codigo}</b> {x.descricao}<span className="text-ink-faint"> · última {dataBR(x.ultima_saida)}</span></span>
                  <span className="tabular-nums text-right">{brl(x.valor)}</span>
                </div>
              ))}
            </div>
          ) : null}
          {c.m12?.n_produtos > (c.m12?.produtos || []).length ? <span className="text-[11px] text-ink-faint">Mostrando os {c.m12.produtos.length} de maior valor, de {c.m12.n_produtos} produtos.</span> : null}
          <span className="text-[11px] text-ink-faint">{c.criterio} Medido em {dataHora(item.ficha?.compras_medido_em)}.</span>
        </div>
      ) : null}
    </div>
  );
}

// Copiloto de ligacao (21/10/2026): roteiro gerado pela IA do Groq so com os
// dados deste cartao. Fica guardado; "Refazer" gera de novo. So leitura.
function Copiloto({ item, chamar, autoIniciar }) {
  const [roteiro, setRoteiro] = useState(item.roteiro_ia || null);
  const [quando, setQuando] = useState(item.roteiro_em || '');
  const [quem, setQuem] = useState(item.roteiro_por || '');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const gerar = useCallback(async (refazer) => {
    setCarregando(true); setErro('');
    try {
      const d = await chamar('POST', `/recuperacao/roteiro/${encodeURIComponent(item.budget_id)}`, { refazer });
      setRoteiro(d.roteiro || null); setQuando(d.roteiro_em || ''); setQuem(d.roteiro_por || '');
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [chamar, item.budget_id]);
  const iniciou = useRef(false);
  useEffect(() => {
    if (autoIniciar && !roteiro && !iniciou.current) { iniciou.current = true; gerar(false); }
  }, [autoIniciar, roteiro, gerar]);
  const titulo = 'text-[11px] uppercase tracking-[0.14em] text-ink-faint font-extrabold';
  return (
    <div className="flex flex-col gap-2 pb-3 border-b border-line" aria-label="Copiloto de ligação">
      <div className="flex items-center gap-2 flex-wrap">
        <p className={titulo}>Copiloto de ligação</p>
        <span className="flex-1" />
        {roteiro ? (
          <button type="button" disabled={carregando} onClick={() => gerar(true)} className="px-3 py-1 rounded-full border border-line bg-surface-card text-brand-ink text-xs font-bold disabled:opacity-60">Refazer</button>
        ) : (
          <button type="button" disabled={carregando} onClick={() => gerar(false)} className="px-3 py-1 rounded-full bg-brand-600 text-white text-xs font-bold disabled:opacity-60">Preparar ligação</button>
        )}
      </div>
      {carregando ? <p className="text-sm text-ink-soft">Preparando o roteiro...</p> : null}
      {erro ? <p className="text-sm font-semibold text-risk-ink">{erro}</p> : null}
      {roteiro && !carregando ? (
        <div className="flex flex-col gap-2 text-[13px] text-ink">
          {roteiro.situacao ? <p className="rounded-lg bg-surface-low border border-line px-3 py-2"><b>Situação:</b> {roteiro.situacao}</p> : null}
          {(roteiro.comprados_depois || []).length ? (
            <p className="rounded-lg bg-ok border border-ok-line text-ok-ink px-3 py-2 text-xs font-semibold">
              Comprou depois, com a BHS, item(ns) deste orçamento: {roteiro.comprados_depois.map((c) => `${c.codigo} (saída ${dataBR(c.ultima_saida)})`).join(', ')}. Calculado pelo CRM, sem IA.
            </p>
          ) : null}
          <div><b>Abertura</b><p>“{roteiro.abertura}”</p></div>
          <div><b>Argumentos</b><ol className="list-decimal pl-5">{(roteiro.argumentos || []).map((a, i) => <li key={i}>{a}</li>)}</ol></div>
          <div><b>Objeções</b>{(roteiro.objecoes || []).map((o, i) => <p key={i}><i>“{o.objecao}”</i> → {o.resposta}</p>)}</div>
          <div><b>Fechamento</b><p>“{roteiro.fechamento}”</p></div>
          {(roteiro.confirmar || []).length ? <div className="text-warn-ink"><b>Confirmar antes de afirmar</b><ul className="list-disc pl-5">{roteiro.confirmar.map((a, i) => <li key={i}>{a}</li>)}</ul></div> : null}
          <p className="text-[11px] text-ink-faint">Sugestão gerada por IA ({roteiro.modelo}) só com os dados deste cartão{quando ? `, em ${dataHora(quando)}` : ''}{quem ? ` por ${quem}` : ''}. Confira antes de afirmar qualquer dado técnico ou comercial.</p>
        </div>
      ) : null}
    </div>
  );
}

function Gaveta({ item, soFicha, copiloto, itens, hoje, user, nomesVendedores, chamar, aoFechar, aoSalvar }) {
  const f = item.ficha || {};
  const [resultado, setResultado] = useState('');
  const [falouCom, setFalouCom] = useState('');
  const [nota, setNota] = useState('');
  const [retorno, setRetorno] = useState('');
  const [resumo, setResumo] = useState(item.resumo || '');
  const [contato, setContato] = useState(f.contato || '');
  const [telefone, setTelefone] = useState(f.telefone || '');
  const [email, setEmail] = useState(f.email || '');
  const [site, setSite] = useState(f.site || '');
  const [info, setInfo] = useState(f.info || '');
  const [vendedor, setVendedor] = useState(item.vendedor || '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !salvando) aoFechar(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [aoFechar, salvando]);

  const historico = (item.interacoes || []);
  const outros = itens.filter((x) => x.cod_cliente && x.cod_cliente === item.cod_cliente && x.budget_id !== item.budget_id).map((x) => x.orcamento_erp);

  const salvar = async () => {
    setErro('');
    const algoDaLigacao = !soFicha && (falouCom.trim() || nota.trim() || retorno);
    if (!soFicha && !resultado && algoDaLigacao) { setErro('Escolha o resultado da ligação, ou apague os campos da ligação para salvar só a ficha.'); return; }
    if (!soFicha && resultado === 'pedir_retorno' && !retorno) { setErro('Informe a data de retorno.'); return; }
    const mudouFicha = ['contato', 'telefone', 'email', 'site', 'info'].some((k) => ({ contato, telefone, email, site, info })[k].trim() !== String(f[k] || ''));
    const mudouResumo = resumo.trim() !== String(item.resumo || '');
    const vendedorFinal = vendedor || (!soFicha && resultado && !item.vendedor ? String(user?.name || '').trim() : item.vendedor || '');
    const mudouVendedor = vendedorFinal !== String(item.vendedor || '');
    if (!(!soFicha && resultado) && !mudouFicha && !mudouResumo && !mudouVendedor) { setErro('Nada mudou. Escolha um resultado ou altere a ficha.'); return; }

    setSalvando(true);
    const feito = [];
    try {
      if (!soFicha && resultado) {
        await chamar('POST', `/budgets/${encodeURIComponent(item.budget_id)}/interactions`, {
          channel: 'Ligação', resultado, falou_com: falouCom.trim(), retorno_em: retorno, notes: nota.trim(),
        });
        feito.push('ligação registrada');
      }
      if (mudouResumo || mudouVendedor) {
        const corpo = {};
        if (mudouResumo) corpo.resumo = resumo.trim();
        if (mudouVendedor) corpo.vendedor = vendedorFinal;
        await chamar('PUT', `/recuperacao/fila/${encodeURIComponent(item.budget_id)}`, corpo);
        feito.push(mudouResumo ? 'resumo salvo' : 'vendedor salvo');
      }
      if (mudouFicha && item.cod_cliente) {
        await chamar('PUT', `/recuperacao/ficha/${encodeURIComponent(item.cod_cliente)}`, {
          contato: contato.trim(), telefone: telefone.trim(), email: email.trim(), site: site.trim(), info: info.trim(),
        });
        feito.push('ficha atualizada');
      }
      await aoSalvar(`Orçamento ${item.orcamento_erp}: ${feito.join(', ')}`);
    } catch (e) {
      setErro(`${feito.length ? `Já salvo: ${feito.join(', ')}. ` : ''}Não salvou o resto: ${e.message}`);
      setSalvando(false);
    }
  };

  const rotulo = 'text-xs font-bold text-ink-soft';
  const campo = 'w-full px-3 py-2 border border-line rounded-lg text-sm bg-surface-card text-ink';
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={() => !salvando && aoFechar()} />
      <aside role="dialog" aria-modal="true" aria-label={soFicha ? 'Editar ficha do cliente' : 'Registrar ligação'}
        className="absolute top-0 right-0 bottom-0 w-full max-w-[460px] bg-surface-card border-l border-line flex flex-col">
        <div className="px-5 py-4 border-b border-line">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-faint font-bold tabular-nums">#{item.ordem} · Orçamento {item.orcamento_erp} · {brl(item.orcamento?.budget_value)}</p>
          <p className="font-bold text-ink text-[15.5px] break-words">{item.orcamento?.client_name || f.nome_cliente}</p>
          <p className="text-xs text-ink-soft">{[f.telefone, ...separar(f.telefones_erp, ' / ')].filter(Boolean).join(' · ') || 'sem telefone'}</p>
          <div className="mt-2 inline-flex items-baseline gap-2 px-3 py-1.5 rounded-xl border border-info-line bg-brand-50">
            <span className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint">Falar com</span>
            <b className="text-base text-ink">{f.contato || 'contato não cadastrado'}</b>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-3">
          {!soFicha ? <Copiloto item={item} chamar={chamar} autoIniciar={copiloto} /> : null}
          <Produtos item={item} />
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-faint font-extrabold">Ficha</p>
          <label className="flex flex-col gap-1"><span className={rotulo}>Resumo da negociação <span className="font-normal text-ink-faint">· deste orçamento</span></span>
            <textarea className={`${campo} min-h-[80px]`} maxLength={400} value={resumo} onChange={(e) => setResumo(e.target.value)} placeholder="Em que pé está a conversa. Ex.: quer 10% de desconto no lote de 50; aguarda aprovação do diretor." />
            {item.resumo_por ? <span className="text-[11.5px] text-ink-faint">Última edição: {item.resumo_por} · {dataHora(item.resumo_em)}</span> : null}
          </label>
          <label className="flex flex-col gap-1"><span className={rotulo}>Contato principal <span className="font-normal text-ink-faint">· nome e cargo; vale para todos os orçamentos deste cliente</span></span>
            <input className={campo} maxLength={120} value={contato} onChange={(e) => setContato(e.target.value)} placeholder="Ex.: Anderson (compras)" />
          </label>
          <label className="flex flex-col gap-1"><span className={rotulo}>Telefone do contato</span>
            <input className={campo} maxLength={40} inputMode="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="Direto ou celular, se ele passar" />
          </label>
          <label className="flex flex-col gap-1"><span className={rotulo}>E-mail do contato</span>
            <input className={campo} maxLength={120} inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@empresa.com.br" />
            <span className="text-[11.5px] text-ink-faint">Telefone e e-mail da ficha aparecem primeiro na fila. Não alteram o cadastro do AM Gestor.</span>
          </label>
          <label className="flex flex-col gap-1"><span className={rotulo}>Site do cliente</span>
            <input className={campo} maxLength={200} inputMode="url" value={site} onChange={(e) => setSite(e.target.value)} placeholder="www.empresa.com.br" />
          </label>
          <label className="flex flex-col gap-1"><span className={rotulo}>Informações importantes do cliente</span>
            <textarea className={`${campo} min-h-[80px]`} maxLength={800} value={info} onChange={(e) => setInfo(e.target.value)} placeholder="Quem decide a compra, horário que atende, se compra para revenda ou uso, histórico relevante." />
            <span className="text-[11.5px] text-ink-faint">{f.editado_por ? `Última edição: ${f.editado_por} · ${dataHora(f.editado_em)}. ` : ''}{outros.length ? `Também aparece no orçamento ${outros.join(', ')}.` : ''}</span>
          </label>

          {!soFicha && (
            <>
              <p className="mt-1 pt-3 border-t border-line text-[11px] uppercase tracking-[0.14em] text-ink-faint font-extrabold">Ligação de agora <span className="normal-case tracking-normal font-medium">· deixe em branco se só está atualizando a ficha</span></p>
              <div className="flex flex-col gap-1">
                <span className={rotulo} id="rec-res">Resultado da ligação</span>
                <div role="radiogroup" aria-labelledby="rec-res" className="flex gap-1.5 flex-wrap">
                  {Object.entries(RESULTADOS).map(([k, r]) => (
                    <button key={k} type="button" role="radio" aria-checked={resultado === k} onClick={() => setResultado(resultado === k ? '' : k)}
                      className={`px-3 py-1.5 rounded-full border text-[12.5px] font-semibold ${resultado === k ? 'bg-brand-600 border-brand-600 text-white' : 'bg-surface-card border-line text-ink'}`}>{r.rotulo}</button>
                  ))}
                </div>
              </div>
              <label className="flex flex-col gap-1"><span className={rotulo}>Falou com</span>
                <input className={campo} maxLength={120} value={falouCom} onChange={(e) => setFalouCom(e.target.value)} placeholder="Nome e cargo de quem atendeu" />
              </label>
              <label className="flex flex-col gap-1"><span className={rotulo}>Anotação</span>
                <textarea className={`${campo} min-h-[80px]`} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="O que o cliente disse, objeção, próximo passo" />
              </label>
              <label className="flex flex-col gap-1"><span className={rotulo}>Retornar em</span>
                <input type="date" className={campo} min={hoje} value={retorno} onChange={(e) => setRetorno(e.target.value)} />
                <span className="text-[11.5px] text-ink-faint">{resultado === 'pedir_retorno' ? 'Obrigatório para "Pedir retorno".' : 'Opcional. Preencha se combinou de ligar de novo.'}</span>
              </label>
              <label className="flex flex-col gap-1"><span className={rotulo}>Vendedor desta fila</span>
                <select className={campo} value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
                  <option value="">{item.vendedor ? 'Sem vendedor' : `Quem registrar (${user?.name || 'você'})`}</option>
                  {nomesVendedores.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <p className="text-[11.5px] text-ink-faint">Vai gravar no orçamento: canal "Ligação", data e hora de agora e o seu login como autor.</p>
            </>
          )}
          {erro ? <p className="text-sm font-semibold text-risk-ink">{erro}</p> : null}
          {!soFicha && (
            <div className="flex flex-col gap-1.5">
              <span className={rotulo}>Ligações e interações anteriores</span>
              {historico.length ? historico.map((h) => (
                <div key={h.id} className="text-xs px-3 py-2 rounded-lg border border-line bg-surface-low">
                  <b>{h.resultado ? RESULTADOS[h.resultado]?.rotulo : h.channel}</b> · {dataHora(h.interaction_at)} · {h.operator}
                  {h.falou_com ? ` · falou com ${h.falou_com}` : ''}{h.retorno_em ? ` · retornar ${diaMes(h.retorno_em)}` : ''}
                  {h.notes ? <div className="break-words">{h.notes}</div> : null}
                </div>
              )) : <span className="text-xs text-ink-faint">Nenhuma interação neste orçamento.</span>}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-line flex justify-end gap-2 flex-wrap">
          <button type="button" disabled={salvando} onClick={aoFechar} className="px-4 py-2 rounded-full border border-line bg-surface-card text-brand-ink text-sm font-bold">Cancelar</button>
          <button type="button" disabled={salvando} onClick={salvar} className="px-4 py-2 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold disabled:opacity-60">{salvando ? 'Salvando...' : (soFicha ? 'Salvar ficha' : 'Salvar no CRM')}</button>
        </div>
      </aside>
    </div>
  );
}
